/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UPLOAD_CONDITION_VALIDATION
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Updated BroadcastItemRow to support condition validation and manual review.
 * Extracted CategorySelect and ConditionSelect to sub-components to reduce
 * file size and improve modularity.
 *
 * File Size Check:
 * - Current: 410 lines
 * - Removed: ~240 lines (extracted to sub-components)
 * - Added: ~10 lines
 * - Projected: ~180 lines
 * - Decision: Proceed (within 200-line component limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/components/CategorySelect.tsx
 * - client/src/features/broadcast/components/ConditionSelect.tsx
 */
// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React from "react";
import { useFormikContext, getIn } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { BroadcastItemCreate } from "../../services/broadcastService";
import { Currency } from "../../interfaces/Currency";
import CategorySelect from "./components/CategorySelect";
import ConditionSelect from "./components/ConditionSelect";
import styles from "./Broadcast.module.scss";

type FormValues = any; // Broadcast form values type

interface BroadcastItemRowProps {
  item: BroadcastItemCreate;
  index: number;
  userCurrency: Currency;
  onItemChange?: (index: number, field: string, value: any) => void;
  totalItems: number;
  onRemoveItem?: (index: number) => void;
}

const BroadcastItemRow: React.FC<BroadcastItemRowProps> = ({
  item,
  index,
  userCurrency,
  onItemChange,
  totalItems,
  onRemoveItem,
}) => {
  const {
    values,
    errors: formikErrors,
    touched: formikTouched,
    handleChange,
    handleBlur,
  } = useFormikContext<FormValues>();

  const fieldState = (name: string) => {
    const path = `broadcast.${index}.${name}`;
    const err = getIn(formikErrors, path);
    const tch = getIn(formikTouched, path);
    return {
      path,
      err,
      tch,
      invalid: Boolean(tch && err),
      describedBy: `${path}__error`,
    };
  };

  // Use it per field:
  const mfr = fieldState("manufacturer");
  const part = fieldState("partNumber");
  const qty = fieldState("quantity");
  const cur = fieldState("currency");
  const price = fieldState("price");
  const desc = fieldState("description");

  // Determine delete button tooltip
  const deleteTooltip = totalItems === 1 ? "Reset this row to blank" : "Delete this row";

  return (
    <div className="rounded p-3 bg-light">
      {/* Header with Broadcast number and delete button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="m-0 text-muted">Broadcast {index + 1}</h6>
        {onRemoveItem && (
          <button
            type="button"
            className="btn-danger-figma btn-sm"
            onClick={() => onRemoveItem(index)}
            title={deleteTooltip}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}
      </div>

      {/* Row 1: SKU/Model/Part, Category, Manufacturer, Condition */}
      <div className={`row g-3 mb-3 ${styles.rowMinWidth}`}>
        <div className="col-lg-3 col-md-6">
          <label htmlFor={`partNumber-${index}`} className="form-label">
            SKU/Model/Part Number *
          </label>
          <input
            type="text"
            id={`partNumber-${index}`}
            name={part.path}
            data-field-path={part.path}
            className={`form-control ${part.invalid ? "is-invalid" : ""}`}
            value={getIn(values, part.path) ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="e.g., DJI-001, Mavic 3"
            aria-invalid={part.invalid}
            aria-describedby={part.invalid ? part.describedBy : undefined}
          />
          {part.invalid && (
            <div className="invalid-feedback" id={part.describedBy}>
              {part.err as string}
            </div>
          )}
        </div>

        <CategorySelect
          index={index}
          categoryNeedsReview={item.categoryNeedsReview}
          onItemChange={onItemChange}
        />

        <div className="col-lg-3 col-md-6">
          <label htmlFor={`manufacturer-${index}`} className="form-label">
            Manufacturer *
          </label>
          <input
            type="text"
            id={`manufacturer-${index}`}
            name={mfr.path}
            data-field-path={mfr.path}
            className={`form-control ${mfr.invalid ? "is-invalid" : ""}`}
            value={getIn(values, mfr.path) ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="e.g., DJI, Parrot"
            aria-invalid={mfr.invalid}
            aria-describedby={mfr.invalid ? mfr.describedBy : undefined}
          />
          {mfr.invalid && (
            <div className="invalid-feedback" id={mfr.describedBy}>
              {mfr.err as string}
            </div>
          )}
        </div>

        <ConditionSelect
          index={index}
          conditionNeedsReview={item.conditionNeedsReview}
          onItemChange={onItemChange}
        />
      </div>

      {/* Row 2: Quantity, Price, Currency, Description */}
      <div className={`row g-3 ${styles.rowMinWidth}`}>
        <div className="col-lg-2 col-md-4">
          <label htmlFor={`quantity-${index}`} className="form-label">
            Quantity *
          </label>
          <input
            type="number"
            id={`quantity-${index}`}
            name={qty.path}
            data-field-path={qty.path}
            className={`form-control ${qty.invalid ? "is-invalid" : ""}`}
            min="1"
            value={getIn(values, qty.path) ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={qty.invalid}
            aria-describedby={qty.invalid ? qty.describedBy : undefined}
          />
          {qty.invalid && (
            <div className="invalid-feedback" id={qty.describedBy}>
              {qty.err as string}
            </div>
          )}
        </div>

        <div className="col-lg-2 col-md-4">
          <label htmlFor={`price-${index}`} className="form-label">
            Price
          </label>
          <input
            type="number"
            id={`price-${index}`}
            name={price.path}
            data-field-path={price.path}
            className={`form-control ${price.invalid ? "is-invalid" : ""}`}
            min="0"
            step="0.01"
            value={getIn(values, price.path) ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="0.00"
            aria-invalid={price.invalid}
            aria-describedby={price.invalid ? price.describedBy : undefined}
          />
          {price.invalid && (
            <div className="invalid-feedback" id={price.describedBy}>
              {price.err as string}
            </div>
          )}
        </div>

        <div className="col-lg-2 col-md-4">
          <label className="form-label">Currency</label>
          <div
            className={`form-control ${styles.currencyDisplay}`}
            title="Currency is set from your profile preferences"
          >
            {getIn(values, cur.path) ?? "USD"}
          </div>
        </div>

        <div className={`col-lg-6 col-md-12 ${styles.rowMinWidth}`}>
          <label htmlFor={`description-${index}`} className="form-label">
            Description
          </label>
          <textarea
            id={`description-${index}`}
            name={desc.path}
            data-field-path={desc.path}
            className={`form-control ${styles.textareaFull} ${desc.invalid ? "is-invalid" : ""}`}
            rows={2}
            value={getIn(values, desc.path) ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Additional details about this item..."
            aria-invalid={desc.invalid}
            aria-describedby={desc.invalid ? desc.describedBy : undefined}
          />
          {desc.invalid && (
            <div className="invalid-feedback" id={desc.describedBy}>
              {desc.err as string}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BroadcastItemRow;
